import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FilterConfig } from '../components/asset-filter/asset-filter.component';
import { CollectionTab } from '../components/asset-grid/asset-grid.component';

export type CollectionPageKey = 'datasets' | 'models';

export interface CollectionListingSavedState {
  selectedFilters: Record<string, string[]>;
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
  sortOption: string;
  viewMode: 'grid' | 'list' | 'table';
  collectionTab: CollectionTab;
  /** Filter sidebar section ids that were expanded before navigating away. */
  expandedFilterSections: string[];
  /** Mobile filter drawer/modal open state before navigating away. */
  isMobileFilterOpen: boolean;
}

/** Extract checked filter option labels keyed by section id. */
export function extractSelectedFilters(config: FilterConfig): Record<string, string[]> {
  const selected: Record<string, string[]> = {};
  for (const section of config.sections) {
    if (!section.options?.length) {
      continue;
    }
    const labels = section.options.filter((opt) => opt.checked).map((opt) => opt.label);
    if (labels.length > 0) {
      selected[section.id] = labels;
    }
  }
  return selected;
}

/** Collect expanded filter section ids from the sidebar accordion. */
export function extractExpandedFilterSections(config: FilterConfig): string[] {
  return config.sections.filter((section) => section.isExpanded).map((section) => section.id);
}

/** Restore expanded/collapsed state for filter sidebar sections. */
export function applyExpandedFilterSections(
  config: FilterConfig,
  sectionIds: string[],
): void {
  const expandedSet = new Set(sectionIds);
  for (const section of config.sections) {
    section.isExpanded = expandedSet.has(section.id);
  }
}

/** Expand sections that have checked options so applied filters stay visible after restore. */
export function expandSectionsWithActiveFilters(config: FilterConfig): void {
  for (const section of config.sections) {
    if (section.options?.some((option) => option.checked)) {
      section.isExpanded = true;
    }
  }
}

/** Re-apply saved filter selections after filter options are loaded from the API. */
export function applySelectedFilters(
  config: FilterConfig,
  selected: Record<string, string[]>,
): void {
  for (const section of config.sections) {
    const labels = selected[section.id];
    if (!section.options?.length || !labels?.length) {
      continue;
    }
    const labelSet = new Set(labels);
    for (const option of section.options) {
      option.checked = labelSet.has(option.label);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class CollectionListingStateService {
  private readonly router = inject(Router);
  private readonly savedStates = new Map<CollectionPageKey, CollectionListingSavedState>();
  private pendingRestoreKey: CollectionPageKey | null = null;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event) => this.onNavigationStart(event.url));
  }

  saveState(key: CollectionPageKey, state: CollectionListingSavedState): void {
    this.savedStates.set(key, state);
  }

  /** Call before navigating from a collection page to its detail page. */
  markPendingRestore(key: CollectionPageKey): void {
    this.pendingRestoreKey = key;
  }

  /**
   * Returns saved state when returning from a detail page; otherwise clears stored state.
   */
  consumeRestore(key: CollectionPageKey): CollectionListingSavedState | null {
    const shouldRestore = this.pendingRestoreKey === key;
    this.pendingRestoreKey = null;

    if (!shouldRestore) {
      this.savedStates.delete(key);
      return null;
    }

    return this.savedStates.get(key) ?? null;
  }

  clearState(key: CollectionPageKey): void {
    this.savedStates.delete(key);
    if (this.pendingRestoreKey === key) {
      this.pendingRestoreKey = null;
    }
  }

  private onNavigationStart(url: string): void {
    const path = this.normalizePath(url);
    if (this.getCollectionPageKey(path) || this.getDetailPageKey(path)) {
      return;
    }
    this.pendingRestoreKey = null;
    this.savedStates.clear();
  }

  private normalizePath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  private getCollectionPageKey(path: string): CollectionPageKey | null {
    if (path === '/datasets') {
      return 'datasets';
    }
    if (path === '/models') {
      return 'models';
    }
    return null;
  }

  private getDetailPageKey(path: string): CollectionPageKey | null {
    if (/^\/dataset\/[^/]+$/.test(path)) {
      return 'datasets';
    }
    if (/^\/model\/[^/]+$/.test(path)) {
      return 'models';
    }
    return null;
  }
}
